import * as XLSX from "xlsx";

export const IMPORT_COLUMNS = [
  { key: "product_name", label: "Product Name*", width: 28 },
  { key: "category", label: "Category", width: 14 },
  { key: "fabric", label: "Fabric", width: 14 },
  { key: "size", label: "Size", width: 12 },
  { key: "color", label: "Color", width: 12 },
  { key: "price", label: "Price (INR)*", width: 12 },
  { key: "cost_price", label: "Cost Price", width: 12 },
  { key: "stock", label: "Stock", width: 8 },
  { key: "shelf_location", label: "Shelf Location", width: 16 },
  { key: "barcode", label: "Barcode", width: 18 },
  { key: "low_stock_alert", label: "Low Stock Alert", width: 14 },
  { key: "status", label: "Status (in_shelf/draft)", width: 18 },
];

const SAMPLE_ROWS = [
  {
    product_name: "Silk Kurta Set",
    category: "Kurta",
    fabric: "Silk",
    size: "Free Size",
    color: "Red",
    price: 2499,
    cost_price: 1200,
    stock: 3,
    shelf_location: "Rack-A",
    barcode: "",
    low_stock_alert: 2,
    status: "in_shelf",
  },
  {
    product_name: "Silk Kurta Set",
    category: "Kurta",
    fabric: "Silk",
    size: "M",
    color: "Red",
    price: 2599,
    cost_price: 1250,
    stock: 2,
    shelf_location: "Rack-A",
    barcode: "",
    low_stock_alert: 2,
    status: "in_shelf",
  },
  {
    product_name: "Silk Kurta Set",
    category: "Kurta",
    fabric: "Silk",
    size: "L",
    color: "Blue",
    price: 2599,
    cost_price: 1250,
    stock: 1,
    shelf_location: "Rack-A",
    barcode: "8901234567890",
    low_stock_alert: 2,
    status: "in_shelf",
  },
  {
    product_name: "Banarasi Saree",
    category: "Saree",
    fabric: "Silk",
    size: "Free Size",
    color: "Maroon",
    price: 8999,
    cost_price: 4500,
    stock: 1,
    shelf_location: "Rack-B",
    barcode: "",
    low_stock_alert: 1,
    status: "in_shelf",
  },
  {
    product_name: "Cotton Kurti (draft)",
    category: "Kurta",
    fabric: "Cotton",
    size: "XL",
    color: "Green",
    price: "",
    cost_price: "",
    stock: 0,
    shelf_location: "Rack-C",
    barcode: "",
    low_stock_alert: 2,
    status: "draft",
  },
];

const INSTRUCTIONS = [
  ["Vastrakiran — Bulk product import"],
  [""],
  ["One row = one product variant (unique barcode per row)."],
  ["Rows with the same product name can differ by size and color — like the variant matrix."],
  [""],
  ["Required: Product Name. Price is required unless Status is 'draft'."],
  ["Barcode: leave empty to auto-generate. Must be unique if provided."],
  ["Size examples: Free Size, XS, S, M, L, XL, XXL, XXXL"],
  ["Status: in_shelf (default) or draft (hidden from POS until published)"],
  [""],
  ["Delete sample rows before uploading your inventory, or edit them in place."],
];

function normalizeHeader(h) {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[()]/g, "")
    .replace(/\*+/g, "")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

const HEADER_ALIASES = {
  product_name: "product_name",
  name: "product_name",
  product: "product_name",
  low_stock_alert: "low_stock_alert",
  low_stock_threshold: "low_stock_alert",
  low_stock: "low_stock_alert",
  shelf_location: "shelf_location",
  shelf: "shelf_location",
  cost_price: "cost_price",
  cost: "cost_price",
  price_inr: "price",
  price: "price",
};

export function downloadProductImportTemplate() {
  const headers = IMPORT_COLUMNS.map((c) => c.label);
  const dataRows = SAMPLE_ROWS.map((row) =>
    IMPORT_COLUMNS.map((c) => row[c.key] ?? ""),
  );

  const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
  ws["!cols"] = IMPORT_COLUMNS.map((c) => ({ wch: c.width }));

  const wsHelp = XLSX.utils.aoa_to_sheet(INSTRUCTIONS);
  wsHelp["!cols"] = [{ wch: 72 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Products");
  XLSX.utils.book_append_sheet(wb, wsHelp, "Instructions");
  XLSX.writeFile(wb, "vastrakiran-product-import-template.xlsx");
}

export function parseProductImportFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const sheetName = wb.SheetNames.find((n) => n.toLowerCase() === "products") || wb.SheetNames[0];
        const sheet = wb.Sheets[sheetName];
        const raw = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        const rows = raw
          .map((row) => {
            const mapped = {};
            Object.entries(row).forEach(([k, v]) => {
              const norm = normalizeHeader(k);
              const key = HEADER_ALIASES[norm] || norm;
              mapped[key] = v;
            });
            return mapped;
          })
          .filter((r) => String(r.product_name || r.name || "").trim())
          .map((r) => ({
            name: String(r.product_name || r.name || "").trim(),
            category: r.category ? String(r.category).trim() : undefined,
            fabric: r.fabric ? String(r.fabric).trim() : undefined,
            size: r.size ? String(r.size).trim() : undefined,
            color: r.color ? String(r.color).trim() : undefined,
            price: r.price === "" || r.price == null ? 0 : Number(r.price),
            cost_price: r.cost_price === "" || r.cost_price == null ? 0 : Number(r.cost_price),
            stock: r.stock === "" || r.stock == null ? 1 : Number(r.stock),
            shelf_location: r.shelf_location ? String(r.shelf_location).trim() : undefined,
            barcode: r.barcode ? String(r.barcode).trim() : undefined,
            low_stock_threshold:
              r.low_stock_alert === "" || r.low_stock_alert == null
                ? 2
                : Number(r.low_stock_alert),
            status: String(r.status || "in_shelf").trim().toLowerCase() === "draft" ? "draft" : "in_shelf",
          }))
          .filter((r) => r.status === "draft" || r.price > 0);

        resolve(rows);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsArrayBuffer(file);
  });
}
